import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import useSWR from 'swr';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { capturePromiseException } from '~/lib/metrics';
import { P2GAlert, P2GBox, P2GFade, P2GLink, P2GText } from '@parcel2go/core-ui';
import { LABEL_API, TranslationNamespaces } from '~/lib/constants';
import { SidePanelContent } from '~/components/sidepanel-section';
import { Trans, useTranslation } from 'react-i18next';
import { LinkModel } from './book/LinkModel';
import {
  generateHref,
  generateTranslationKey,
  getSanitisedEndpoint,
  isReady,
  labelTranslationMap,
  StatusData
} from './labels/label-lib';
import { SettingsCache } from '~/lib/consts/settings-cache';
import OrderIndividualLabels from './OrderIndividualLabels';

interface props {
  isParent: boolean;
  statusEndpoint: string;
  onCustomerWantsToSeeLabels?: () => void;
  children?: React.ReactNode;
  setPreSignedLinks?: (links?: LinkModel[]) => void;
}

const OrderLabelStatusObserver = ({
  onCustomerWantsToSeeLabels,
  setPreSignedLinks,
  statusEndpoint,
  isParent,
  children
}: props) => {
  const { user } = useAuth();
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const [takingTooLong, setTakingTooLong] = useState(false);
  const [customerWantsToSeeLabels, setCustomerWantsToSeeLabels] = useState(false);

  const onCustomerWantsToSeeLabelsClick = () => {
    if (onCustomerWantsToSeeLabels) {
      onCustomerWantsToSeeLabels();
    }
    setCustomerWantsToSeeLabels(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setTakingTooLong(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  const { data } = useSWR<StatusData>(
    `${LABEL_API}${statusEndpoint}`,
    () =>
      statusEndpoint &&
      authenticatedFetchWithTenantAnalytics(user, tenant, settings, session, getSanitisedEndpoint(statusEndpoint))
        .then((res) => res.json())
        .catch(capturePromiseException),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      revalidateOnMount: false,
      refreshInterval: (d) => (d === undefined ? 1000 : customerWantsToSeeLabels || isReady(d) ? 0 : 2500) // Refresh every second if we dont have a pre-signed URL
    }
  );

  const allReady = useMemo(() => isReady(data) ?? false, [data]);

  const complete = useCallback(() => {
    // Pass back pre-signed links
    if (statusEndpoint?.includes('?') && setPreSignedLinks && data?.result?.labelOptions) {
      const orderLineId = new URLSearchParams(statusEndpoint?.split('?')[1]).get('orderLineIds');
      const orderId = new URLSearchParams(statusEndpoint?.split('?')[1]).get('orderId');

      const labelsToAdd: LinkModel[] = [];

      data.result.labelOptions.forEach((option) => {
        const k = generateTranslationKey(option);
        const sanitisedType = k.replaceAll('_', '-');

        // Standard Label Options
        labelsToAdd.push({
          Href: generateHref(option, tenant, false, orderId, orderLineId),
          Type: `LABELS-${sanitisedType}_PRESIGNED`,
          Label: t(`LINK_${k}`, labelTranslationMap[k])
        });

        // Pick and Pack Labels
        if (SettingsCache.settings.Features.includes('PackageContents')) {
          labelsToAdd.push({
            Href: generateHref(option, tenant, true, orderId, orderLineId),
            Type: `LABELS-PACKING-${sanitisedType}_PRESIGNED`,
            Label: t(`LINK_${k}`, labelTranslationMap[k]),
            LinkGroup: 'LabelWithPackingSlip'
          });
        }
      });

      setPreSignedLinks(labelsToAdd);
    }
  }, [setPreSignedLinks, data, statusEndpoint, t, tenant]);

  useEffect(() => {
    if (allReady) {
      complete();
    }
  }, [allReady, complete]);

  const orderLineStatuses = useMemo(() => {
    return data?.result?.orderLineStatuses || [];
  }, [data]);

  if (allReady || customerWantsToSeeLabels || !statusEndpoint) {
    return (
      <P2GFade in>
        <>
          <>{children}</>
          {isParent && (
            <OrderIndividualLabels
              wrapper={(x) => <P2GBox mt={2}>{x}</P2GBox>}
              statuses={orderLineStatuses}
              link={t('ORDER_INDIVIDUAL_LABELS_LINK', 'View Individual Labels')}
            />
          )}
        </>
      </P2GFade>
    );
  }

  return (
    <P2GAlert type="info">
      <SidePanelContent>
        <P2GText variant="body2">{t('CHECKING_LABEL_STATUS', { defaultValue: 'Checking label status...' })}</P2GText>
        {takingTooLong && (
          <>
            {statusEndpoint ? (
              <P2GText variant="body2">
                <Trans
                  t={t}
                  i18nKey="LABEL_STATUS_PRESIGNED_INFO"
                  defaults="Its taking longer than expected to get your labels."
                />
                {isParent && (
                  <OrderIndividualLabels
                    wrapper={(x) => <>{x}</>}
                    statuses={orderLineStatuses}
                    link={t('ORDER_INDIVIDUAL_LABELS_LINK', 'View Individual Labels')}
                  />
                )}
              </P2GText>
            ) : (
              <>
                <P2GText variant="body2">
                  <Trans
                    t={t}
                    i18nKey="LABEL_STATUS_INFO"
                    defaults="Its taking longer than expected to get your labels.<br />You can try to download these now if you'd prefer."
                  />
                </P2GText>
                {onCustomerWantsToSeeLabelsClick && (
                  <P2GText variant="body2">
                    <P2GLink onClick={onCustomerWantsToSeeLabelsClick}>
                      {t('TRY_LABELS_LINK', { defaultValue: 'Try Labels' })}
                    </P2GLink>
                  </P2GText>
                )}
              </>
            )}
          </>
        )}
      </SidePanelContent>
    </P2GAlert>
  );
};

export default OrderLabelStatusObserver;
